import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, PlayCircle, CheckCircle, Stethoscope,
  Activity, FileText, FlaskConical, NotebookPen, FolderOpen,
  Syringe, Pill, ClipboardList, Plus, Mic, Wallet, Share2, MessageSquare, ChevronDown, Video, FileEdit, MoreHorizontal, ListTodo,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReferirPacienteModal } from '@/components/red/ReferirPacienteModal';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import { SignosVitalesTab }    from '@/pages/pacientes/SignosVitalesTab';
import { DiagnosticosTab }     from '@/pages/pacientes/DiagnosticosTab';
import { ExploracionesTab }    from '@/pages/pacientes/exploraciones/ExploracionesTab';
import { ExploracionesDinamicasTab } from '@/pages/pacientes/ExploracionesDinamicasTab';
import { RecetasTab }          from '@/pages/pacientes/RecetasTab';
import { EstudiosTab }         from '@/pages/pacientes/EstudiosTab';
import { NotasPacienteTab }    from '@/pages/pacientes/NotasPacienteTab';
import { DocumentosPacienteTab } from '@/pages/pacientes/DocumentosPacienteTab';
import { TranscripcionConsulta } from '@/components/ia/TranscripcionConsulta';
import { IkalOrb } from '@/components/ikal/IkalOrb';
import { useIkalStore } from '@/store/ikal.store';
import { CobroTab } from './CobroTab';
import { PresupuestoFormDialog } from '@/pages/finanzas/presupuestos/PresupuestoFormDialog';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';
import { getEstadoCitaInfo } from '@/types/cita';
import { TareasRelacionadasPanel } from '@/components/tareas/TareasRelacionadasPanel';
import { ROLES_TAREAS } from '@/hooks/useTareas';

/* ── Types & constants ───────────────────────────────────────────────────── */

type TabId = 'resumen' | 'signos' | 'diagnosticos' | 'exploracion' | 'formularios' | 'receta' | 'estudios' | 'notas' | 'documentos' | 'cobro' | 'tareas';

const TABS: { id: TabId; label: string; icon: React.ElementType; requiereModulo?: string; soloRolesTareas?: boolean }[] = [
  { id: 'resumen',      label: 'Resumen',       icon: ClipboardList },
  { id: 'signos',       label: 'Signos vitales', icon: Activity, requiereModulo: 'signos_vitales_nucleo' },
  { id: 'diagnosticos', label: 'Diagnósticos',  icon: Stethoscope, requiereModulo: 'diagnosticos_lectura' },
  { id: 'exploracion',  label: 'Exploración',   icon: Syringe },
  { id: 'formularios',  label: 'Formularios clínicos', icon: FileEdit, requiereModulo: 'formularios_dinamicos' },
  { id: 'receta',       label: 'Receta',         icon: Pill, requiereModulo: 'recetas_emitir' },
  { id: 'estudios',     label: 'Estudios',       icon: FlaskConical, requiereModulo: 'estudios_lectura' },
  { id: 'notas',        label: 'Notas',          icon: NotebookPen },
  { id: 'documentos',   label: 'Documentos',     icon: FolderOpen },
  { id: 'cobro',        label: 'Cobro',          icon: Wallet },
  { id: 'tareas',       label: 'Tareas',         icon: ListTodo, soloRolesTareas: true },
];

// Fila fija de pestañas siempre visibles — núcleo clínico NOM-004 (identificadores/nota,
// signos vitales, antecedentes-exploración, diagnóstico, receta). El resto vive en "Más"
// para que la fila nunca crezca ni requiera scroll horizontal.
const PRIMARY_TAB_IDS: TabId[] = ['resumen', 'signos', 'diagnosticos', 'exploracion', 'receta'];

const QUICK_ACTIONS: { label: string; tab: TabId; icon: React.ElementType }[] = [
  { label: 'Signo vital',  tab: 'signos',       icon: Activity },
  { label: 'Diagnóstico',  tab: 'diagnosticos', icon: Stethoscope },
  { label: 'Receta',       tab: 'receta',        icon: Pill },
  { label: 'Estudio',      tab: 'estudios',      icon: FlaskConical },
  { label: 'Nota',         tab: 'notas',         icon: FileText },
];

function calcEdad(fechaNacimiento?: string | null) {
  if (!fechaNacimiento) return null;
  const hoy = new Date();
  const nac = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) edad--;
  return edad;
}

function fmtFechaHora(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
    + ' · ' + d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

/* ── Resumen Tab ─────────────────────────────────────────────────────────── */

function ResumenTab({ cita, canEdit, onSave, isSaving }: {
  cita: any; canEdit: boolean;
  onSave: (dto: { motivo?: string; notas?: string; codigoCie11?: string | null; tituloCie11?: string | null }) => void;
  isSaving: boolean;
}) {
  const [motivo, setMotivo] = useState(cita.motivo ?? '');
  const [notas,  setNotas]  = useState(cita.notas ?? '');
  const [cie11,  setCie11]  = useState<Cie11Result | null>(
    cita.tituloCie11 ? { id: '', theCode: cita.codigoCie11 ?? '', title: cita.tituloCie11 } : null
  );
  const [dirty, setDirty] = useState(false);
  const [showTranscripcion, setShowTranscripcion] = useState(false);

  useEffect(() => {
    setMotivo(cita.motivo ?? '');
    setNotas(cita.notas ?? '');
    setCie11(cita.tituloCie11 ? { id: '', theCode: cita.codigoCie11 ?? '', title: cita.tituloCie11 } : null);
    setDirty(false);
  }, [cita.id]);

  const isActive = canEdit && cita.estado !== 'completada' && cita.estado !== 'cancelada';

  function handleSave() {
    onSave({
      motivo:      motivo || undefined,
      notas:       notas  || undefined,
      codigoCie11: cie11?.theCode || null,
      tituloCie11: cie11?.title   || null,
    });
    setDirty(false);
  }

  return (
    <div className="space-y-6">

      {/* Motivo */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Motivo de consulta
          </label>
          {isActive && (
            <button
              type="button"
              onClick={() => setShowTranscripcion(true)}
              className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2.5 py-1.5 rounded-lg transition-colors font-medium border border-primary-100">
              <Mic size={11} /> Escuchar consulta
            </button>
          )}
        </div>
        {isActive ? (
          <textarea rows={3} value={motivo}
            onChange={e => { setMotivo(e.target.value); setDirty(true); }}
            placeholder="Describe el motivo principal de la consulta..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
        ) : (
          <p className={cn('text-sm', motivo ? 'text-text-primary' : 'text-text-secondary italic')}>
            {motivo || 'Sin motivo registrado'}
          </p>
        )}
      </div>

      {/* CIE-11 */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
          Diagnóstico CIE-11
        </label>
        {isActive ? (
          <Cie11Search value={cie11}
            onChange={v => { setCie11(v); setDirty(true); }}
            placeholder="Buscar diagnóstico..." />
        ) : (
          cie11 ? (
            <div className="flex items-center gap-2">
              {cie11.theCode && (
                <span className="font-mono text-[11px] font-bold bg-primary-50 text-primary-700 border border-primary-100 px-1.5 py-0.5 rounded">
                  {cie11.theCode}
                </span>
              )}
              <span className="text-sm text-text-primary">{cie11.title}</span>
            </div>
          ) : (
            <p className="text-sm text-text-secondary italic">Sin diagnóstico registrado</p>
          )
        )}
      </div>

      {/* Notas clínicas */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-text-secondary mb-2">
          Notas clínicas / evolución
        </label>
        {isActive ? (
          <textarea rows={6} value={notas}
            onChange={e => { setNotas(e.target.value); setDirty(true); }}
            placeholder="Plan de tratamiento, observaciones, instrucciones al paciente..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
        ) : (
          <div className="bg-gray-50 rounded-xl px-4 py-3 min-h-[80px]">
            {notas
              ? <p className="text-sm text-text-primary whitespace-pre-wrap">{notas}</p>
              : <p className="text-sm text-text-secondary italic">Sin notas clínicas</p>}
          </div>
        )}
      </div>

      {/* Save */}
      {isActive && dirty && (
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={isSaving}
            className="flex items-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            Guardar cambios
          </button>
        </div>
      )}

      {showTranscripcion && (
        <TranscripcionConsulta
          citaId={cita.id}
          pacienteId={cita.pacienteId}
          onClose={() => setShowTranscripcion(false)}
        />
      )}
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */

export function ConsultaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const { openIkal } = useIkalStore();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const canEdit = usuario?.rolId === ROL.SUPER_ADMIN || usuario?.rolId === ROL.MEDICO || usuario?.rolId === ROL.MEDICO_ADMIN;

  const visibleTabs = TABS.filter(tab =>
    (!tab.requiereModulo || moduloAplica(tab.requiereModulo, perfilTipo, modulosHabilitados))
    && (!tab.soloRolesTareas || (!!usuario?.rolId && ROLES_TAREAS.includes(usuario.rolId))));
  const primaryTabs = visibleTabs.filter(tab => PRIMARY_TAB_IDS.includes(tab.id));
  const overflowTabs = visibleTabs.filter(tab => !PRIMARY_TAB_IDS.includes(tab.id));
  const visibleQuickActions = QUICK_ACTIONS.filter(qa => {
    const tab = TABS.find(t => t.id === qa.tab);
    return !tab?.requiereModulo || moduloAplica(tab.requiereModulo, perfilTipo, modulosHabilitados);
  });

  const [activeTab, setActiveTab] = useState<TabId>('resumen');
  const activeOverflowTab = overflowTabs.find(tab => tab.id === activeTab);
  const [referirAbierto, setReferirAbierto] = useState(false);
  const [presupFormOpen, setPresupFormOpen] = useState(false);
  const [triggerAdd, setTriggerAdd] = useState<Record<TabId, number>>({
    resumen: 0, signos: 0, diagnosticos: 0, exploracion: 0, formularios: 0,
    receta: 0, estudios: 0, notas: 0, documentos: 0, cobro: 0, tareas: 0,
  });

  /* Queries */
  const { data: cita, isLoading } = useQuery({
    queryKey: ['cita-consulta', id],
    queryFn: () => api.get(`/api/citas/${id}`).then(r => r.data),
    enabled: !!id,
  });

  const pacienteId = cita?.pacienteId;

  const { data: paciente } = useQuery({
    queryKey: ['paciente', pacienteId],
    queryFn: () => api.get(`/api/pacientes/${pacienteId}`).then(r => r.data),
    enabled: !!pacienteId,
  });

  /* Mutations */
  const patchCita = useMutation({
    mutationFn: (dto: any) => api.patch(`/api/citas/${id}`, dto).then(r => r.data),
    onSuccess: data => {
      qc.setQueryData(['cita-consulta', id], data);
      toast.success('Guardado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  /* Quick add: switch to tab + trigger its add modal */
  function handleQuickAdd(tab: TabId) {
    setActiveTab(tab);
    setTriggerAdd(prev => ({ ...prev, [tab]: prev[tab] + 1 }));
  }

  if (isLoading) return (
    <div className="flex items-center justify-center py-24 text-text-secondary text-sm">
      Cargando consulta...
    </div>
  );
  if (!cita) return (
    <div className="flex items-center justify-center py-24 text-text-secondary text-sm">
      Cita no encontrada
    </div>
  );

  const ei = getEstadoCitaInfo(cita.estado);
  const iniciales = paciente
    ? `${paciente.nombre?.charAt(0) ?? ''}${paciente.apellidoPaterno?.charAt(0) ?? ''}`.toUpperCase()
    : 'P';
  const nombreCompleto = paciente
    ? `${paciente.nombre} ${paciente.apellidoPaterno ?? ''} ${paciente.apellidoMaterno ?? ''}`.trim()
    : 'Paciente';
  const edad = calcEdad(paciente?.fechaNacimiento);

  const puedeIniciar    = canEdit && ['espera', 'confirmada'].includes(cita.estado);
  const puedeTerminar   = canEdit && cita.estado === 'en_curso';
  const isActiva        = ['espera', 'confirmada', 'en_curso'].includes(cita.estado);

  return (
    <div className="space-y-0">

      {/* ── Topbar ── */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors">
          <ArrowLeft size={15} /> Volver
        </button>
        <h1 className="text-base font-semibold text-text-primary">Consulta</h1>
        <div className="w-16" />
      </div>

      {/* ── Patient + cita header card ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 md:p-5 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">

          {/* Patient info */}
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button onClick={() => navigate(`/pacientes/${pacienteId}`)}
              className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-primary-100 flex items-center justify-center text-primary-600 text-lg font-bold flex-shrink-0 hover:bg-primary-200 transition-colors">
              {iniciales}
            </button>
            <div className="min-w-0">
              <button onClick={() => navigate(`/pacientes/${pacienteId}`)}
                className="text-base font-bold text-text-primary hover:text-primary-600 transition-colors text-left">
                {nombreCompleto}
              </button>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                {edad !== null && (
                  <span className="text-xs text-text-secondary">{edad} años</span>
                )}
                {paciente?.genero && paciente.genero !== 'no_especificado' && (
                  <span className="text-xs text-text-secondary capitalize">{paciente.genero}</span>
                )}
                {paciente?.tipoSangre && (
                  <span className="text-xs font-bold text-red-600">{paciente.tipoSangre}</span>
                )}
                {paciente?.alergias && (
                  <span className="text-[10.5px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium">
                    ⚠ Alergias
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5">
                <span className="text-xs text-text-secondary">
                  {cita.medico?.nombre ?? '—'}
                </span>
                <span className="text-xs text-text-secondary">·</span>
                <span className="text-xs text-text-secondary capitalize">
                  {fmtFechaHora(cita.fechaInicio)}
                </span>
                <span className="text-xs text-text-secondary">·</span>
                <span className="text-xs text-text-secondary capitalize">
                  {cita.tipo === 'teleconsulta' ? 'Teleconsulta' : 'Presencial'}
                </span>
              </div>
            </div>
          </div>

          {/* Status + action buttons */}
          <div className="flex sm:flex-col items-center sm:items-end gap-2 flex-shrink-0">
            <span className={cn('text-[10.5px] px-2.5 py-1 rounded-full border font-semibold', ei.badgeCls)}>
              {ei.label}
            </span>
            {puedeIniciar && (
              <button
                disabled={patchCita.isPending}
                onClick={() => patchCita.mutate({ estado: 'en_curso' })}
                className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors">
                <PlayCircle size={14} /> Iniciar consulta
              </button>
            )}
            {puedeTerminar && (
              <button
                disabled={patchCita.isPending}
                onClick={() => patchCita.mutate({ estado: 'completada' })}
                className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors">
                <CheckCircle size={14} /> Terminar consulta
              </button>
            )}
            {cita.tipo === 'teleconsulta' && puedeTerminar && (
              <button
                onClick={() => window.open(`/teleconsulta/${id}`, '_blank', 'width=1280,height=800,noopener,noreferrer')}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors">
                <Video size={14} /> Unirse a videollamada
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick add buttons ── */}
      {canEdit && isActiva && (
        <div className="flex items-center gap-2 flex-wrap mb-4 py-4">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary mr-1">
            Agregar:
          </span>
          {visibleQuickActions.map(qa => (
            <button key={qa.tab} onClick={() => handleQuickAdd(qa.tab)}
              className="flex items-center gap-1.5 text-xs font-medium border border-dashed border-gray-300 text-text-secondary hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-colors">
              <Plus size={11} />
              <qa.icon size={11} />
              {qa.label}
            </button>
          ))}
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <button
            onClick={() => setReferirAbierto(true)}
            className="flex items-center gap-1.5 text-xs font-medium border border-primary-200 text-primary-600 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Share2 size={11} />
            Referir paciente
          </button>
          <button
            onClick={() => setPresupFormOpen(true)}
            className="flex items-center gap-1.5 text-xs font-medium border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            <ClipboardList size={11} />
            Presupuesto
          </button>
          <button
            onClick={() => openIkal({ citaId: id, pacienteId })}
            className="flex items-center gap-1.5 text-xs font-medium text-white px-3 py-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: '#3d5e5a' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2e4a47'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#3d5e5a'; }}
          >
            <IkalOrb size={14} />
            Ikal
          </button>
        </div>
      )}

      {/* ── Tab navigation ── */}
      {/* Mobile select */}
      <div className="md:hidden relative mb-4">
        <select
          value={activeTab}
          onChange={e => setActiveTab(e.target.value as TabId)}
          className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {visibleTabs.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
        </select>
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
      {/* Desktop pill tabs — núcleo fijo + menú "Más" para el resto (sin scroll horizontal) */}
      <div className="hidden md:flex items-center gap-1 bg-gray-100/60 rounded-xl p-1 mb-4">
        {primaryTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0',
              activeTab === tab.id
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary hover:bg-white/60',
            )}>
            <tab.icon size={12} />
            {tab.label}
          </button>
        ))}

        {overflowTabs.length > 0 && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0',
                  activeOverflowTab
                    ? 'bg-white text-text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-white/60',
                )}
              >
                {activeOverflowTab ? <activeOverflowTab.icon size={12} /> : <MoreHorizontal size={12} />}
                {activeOverflowTab ? activeOverflowTab.label : 'Más'}
                <ChevronDown size={11} />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="start" sideOffset={6}
                className="z-50 min-w-[190px] bg-white rounded-xl shadow-lg border border-gray-100 py-1.5">
                {overflowTabs.map(tab => (
                  <DropdownMenu.Item
                    key={tab.id}
                    onSelect={() => setActiveTab(tab.id)}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer outline-none',
                      activeTab === tab.id ? 'text-primary-700 bg-primary-50/60 font-medium' : 'text-text-primary hover:bg-gray-50',
                    )}
                  >
                    <tab.icon size={13} />
                    {tab.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>

      {/* ── Tab content ── */}
      {pacienteId && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          {activeTab === 'resumen' && (
            <ResumenTab
              cita={cita}
              canEdit={canEdit}
              onSave={dto => patchCita.mutate(dto)}
              isSaving={patchCita.isPending}
            />
          )}
          {activeTab === 'signos' && (
            <SignosVitalesTab
              id={pacienteId}
              canEdit={canEdit}
              noDelete
              triggerAdd={triggerAdd.signos}
            />
          )}
          {activeTab === 'diagnosticos' && (
            <DiagnosticosTab
              pacienteId={pacienteId}
              canEdit={canEdit}
              noDelete
              triggerAdd={triggerAdd.diagnosticos}
            />
          )}
          {activeTab === 'exploracion' && (
            <ExploracionesTab id={pacienteId} canEdit={canEdit} consultaId={id} />
          )}
          {activeTab === 'formularios' && (
            <ExploracionesDinamicasTab id={pacienteId} canEdit={canEdit} consultaId={id} />
          )}
          {activeTab === 'receta' && (
            <RecetasTab
              id={pacienteId}
              canEdit={canEdit}
              paciente={paciente}
              noDelete
              citaId={id}
              triggerAdd={triggerAdd.receta}
            />
          )}
          {activeTab === 'estudios' && (
            <EstudiosTab
              id={pacienteId}
              canEdit={canEdit}
              paciente={paciente}
              noDelete
              triggerAdd={triggerAdd.estudios}
            />
          )}
          {activeTab === 'notas' && (
            <NotasPacienteTab
              id={pacienteId}
              canEdit={canEdit}
              noDelete
              triggerAdd={triggerAdd.notas}
            />
          )}
          {activeTab === 'documentos' && (
            <DocumentosPacienteTab id={pacienteId} canEdit={canEdit} />
          )}
          {activeTab === 'tareas' && cita && (
            // La pantalla de consulta se identifica por la cita; el backend suma las tareas ligadas a su consulta.
            <TareasRelacionadasPanel
              entidadTipo="cita"
              entidadId={cita.id}
              etiqueta={`Cita · ${new Date(cita.fechaInicio).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}`}
            />
          )}
          {activeTab === 'cobro' && cita && (
            <CobroTab
              citaId={cita.id}
              pacienteId={pacienteId}
              clinicaId={cita.clinicaId}
              canEdit={canEdit || usuario?.rolId === ROL.RECEPCIONISTA}
            />
          )}
        </div>
      )}

      {/* Modal referir paciente */}
      {referirAbierto && pacienteId && (
        <ReferirPacienteModal
          pacienteId={pacienteId}
          onClose={() => setReferirAbierto(false)}
        />
      )}

      {/* Modal presupuesto */}
      {pacienteId && (
        <PresupuestoFormDialog
          open={presupFormOpen}
          onOpenChange={setPresupFormOpen}
          pacienteId={pacienteId}
          consultaId={id}
        />
      )}
    </div>
  );
}
